import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Row,
  Space,
  Switch,
  Tag,
  message,
} from 'antd';
import { captureAnalytics } from '../../../actions/preload';

// Settings modal for the three-layer EMR auto-upload category whitelist.
// When the switch is off, every category auto-uploads (default); when on,
// only checked categories do — including none at all.
const settingEnabled = (enabled) => enabled === true || enabled === 'true';

const EmrAutoUploadCategorySettings = ({
  settings,
  updateSettings,
  autoEnable,
  canConfigureCategories,
  canConfigureUploadDedup,
}) => {
  const [draft, setDraft] = useState({
    enabled: false,
    categories: [],
    uploadDedupEnabled: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft({
      enabled: typeof autoEnable === 'boolean' ? autoEnable : settingEnabled(settings.enabled),
      categories: [...(settings.categories || [])],
      uploadDedupEnabled: settingEnabled(settings.upload_dedup_enabled),
    });
  }, [settings, autoEnable]);

  // Both outcomes emit the same event with the same shape, so the failure count has a
  // denominator — a failure-only event would be a numerator nobody can interpret.
  // Counts rather than category names: names are organization configuration, and the
  // product question ("do orgs actually restrict, and to how many?") only needs counts.
  const trackSave = (succeeded) => captureAnalytics('emr_auto_upload_settings_saved', {
    succeeded,
    configured_categories: canConfigureCategories,
    configured_upload_dedup: canConfigureUploadDedup,
    whitelist_enabled: draft.enabled,
    category_count: draft.enabled ? draft.categories.length : 0,
  });

  const handleSave = async () => {
    setSaving(true);

    try {
      const updatedSettings = {};
      if (canConfigureCategories) {
        updatedSettings.enabled = draft.enabled;
        updatedSettings.categories = draft.enabled ? draft.categories : [];
      }
      if (canConfigureUploadDedup) {
        updatedSettings.upload_dedup_enabled = draft.uploadDedupEnabled;
      }

      // `crud` RESOLVES on HTTP failure with `{ ...axiosError, error: true }` rather
      // than rejecting, so a caller that hands us the raw action would otherwise fall
      // through to the success toast on a 403/422. Checking here rather than in each
      // caller keeps that impossible to forget; callers that already convert the flag
      // into a throw still land in the same catch.
      const result = await updateSettings(updatedSettings);
      if (result?.error) throw result;
      trackSave(true);
      message.success('Settings saved successfully');
    } catch (error) {
      trackSave(false);
      const errorMsg = error?.response?.data?.error || 'Failed to save settings';
      message.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const staleCategories = settings.stale_categories || [];
  const categoryOptions = [...new Set([
    ...(settings.available_categories || []),
    ...staleCategories,
  ])];

  return (
    <div>
      {canConfigureCategories && (
        <>
          <Alert
            type="info"
            showIcon
            message="Choose which document categories are automatically uploaded to your EHR. Documents in unselected categories stay in Medsender for manual review."
            style={{ marginBottom: 16 }}
          />

          <div style={{ marginBottom: 16 }}>
            <Space>
              <span>Only auto-upload selected categories:</span>
              <Switch
                checked={draft.enabled}
                onChange={(checked) => setDraft((previous) => ({ ...previous, enabled: checked }))}
              />
            </Space>
          </div>

          {draft.enabled && (
            <Checkbox.Group
              value={draft.categories}
              onChange={(categories) => setDraft((previous) => ({ ...previous, categories }))}
              style={{ width: '100%', marginBottom: 16 }}
            >
              <Row gutter={[8, 8]}>
                {categoryOptions.map((category) => (
                  <Col span={12} key={category}>
                    <Checkbox value={category}>
                      {category}
                      {staleCategories.includes(category) && (
                        <Tag color="orange" style={{ marginLeft: 8 }}>no longer in use</Tag>
                      )}
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          )}

          {draft.enabled && draft.categories.length === 0 && (
            <Alert
              type="warning"
              showIcon
              message="No categories selected — no documents will auto-upload."
              style={{ marginBottom: 16 }}
            />
          )}
        </>
      )}

      {canConfigureUploadDedup && (
        <div style={{ marginBottom: 16 }}>
          <Space>
            <span>Skip duplicate pharmacy request uploads:</span>
            <Switch
              checked={draft.uploadDedupEnabled}
              onChange={(checked) => {
                captureAnalytics('emr_auto_upload_dedup_toggled', { enabled: checked });
                setDraft((previous) => ({
                  ...previous,
                  uploadDedupEnabled: checked,
                }));
              }}
            />
          </Space>
          <div style={{ color: '#666', marginTop: 4 }}>
            When a pharmacy request for the same patient and medication was already uploaded or
            tasked within the last 7 days, the new document stays in Medsender for manual review
            instead of being uploaded again. Applies to automatic uploads only — manual uploads
            are never skipped.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Button
          type="primary"
          loading={saving}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

EmrAutoUploadCategorySettings.defaultProps = {
  settings: {},
  autoEnable: null,
  canConfigureCategories: true,
  canConfigureUploadDedup: true,
};

EmrAutoUploadCategorySettings.propTypes = {
  settings: PropTypes.shape({
    emr: PropTypes.string,
    enabled: PropTypes.bool,
    categories: PropTypes.arrayOf(PropTypes.string),
    available_categories: PropTypes.arrayOf(PropTypes.string),
    stale_categories: PropTypes.arrayOf(PropTypes.string),
    upload_dedup_enabled: PropTypes.bool,
  }),
  updateSettings: PropTypes.func.isRequired,
  autoEnable: PropTypes.bool,
  canConfigureCategories: PropTypes.bool,
  canConfigureUploadDedup: PropTypes.bool,
};

export default EmrAutoUploadCategorySettings;
